import { Minus, Plus } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

interface BaristaCartCountFieldsProps {
  baristaCount: number
  cartCount: number
  onBaristaCountChange: (value: number) => void
  onCartCountChange: (value: number) => void
  onBaristaCountBlur?: () => void
  onCartCountBlur?: () => void
  disabled?: boolean
}

/**
 * BaristaCartCountFields - Side-by-side barista and cart count inputs with validation.
 *
 * Enforces: cart count cannot exceed barista count.
 * When barista count decreases below cart count, cart count is auto-clamped.
 * The cart count + button is disabled when cart count >= barista count.
 */
export function BaristaCartCountFields({
  baristaCount,
  cartCount,
  onBaristaCountChange,
  onCartCountChange,
  onBaristaCountBlur,
  onCartCountBlur,
  disabled = false,
}: BaristaCartCountFieldsProps) {
  const handleBaristaChange = (newValue: number) => {
    onBaristaCountChange(newValue)
    if (cartCount > newValue) {
      onCartCountChange(newValue)
    }
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="grid gap-3">
        <Label htmlFor="barista-count">Barista Count</Label>
        <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-none border-r border-input"
            onClick={() => handleBaristaChange(Math.max(0, baristaCount - 1))}
            disabled={disabled || baristaCount <= 0}
          >
            <Minus className="h-4 w-4" />
            <span className="sr-only">Decrease by 1</span>
          </Button>
          <Input
            id="barista-count"
            type="number"
            min="0"
            value={baristaCount}
            onChange={(e) => handleBaristaChange(Number(e.target.value) || 0)}
            onBlur={(e) => {
              const value = Math.max(0, Number(e.target.value) || 0)
              handleBaristaChange(value)
              onBaristaCountBlur?.()
            }}
            disabled={disabled}
            placeholder="0"
            className="border-0 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-none border-l border-input"
            onClick={() => handleBaristaChange(baristaCount + 1)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4" />
            <span className="sr-only">Increase by 1</span>
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        <Label htmlFor="cart-count">Cart Count</Label>
        <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-none border-r border-input"
            onClick={() => onCartCountChange(Math.max(0, cartCount - 1))}
            disabled={disabled || cartCount <= 0}
          >
            <Minus className="h-4 w-4" />
            <span className="sr-only">Decrease by 1</span>
          </Button>
          <Input
            id="cart-count"
            type="number"
            min="0"
            value={cartCount}
            onChange={(e) => {
              const value = Number(e.target.value) || 0
              onCartCountChange(Math.min(Math.max(0, value), baristaCount))
            }}
            onBlur={(e) => {
              const value = Number(e.target.value) || 0
              const clampedValue = Math.min(Math.max(0, value), baristaCount)
              onCartCountChange(clampedValue)
              onCartCountBlur?.()
            }}
            disabled={disabled}
            placeholder="0"
            className="border-0 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-none border-l border-input"
            onClick={() => onCartCountChange(cartCount + 1)}
            disabled={disabled || cartCount >= baristaCount}
          >
            <Plus className="h-4 w-4" />
            <span className="sr-only">Increase by 1</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
